import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, AlertTriangle, RefreshCw, ClipboardCheck, UserCheck } from "lucide-react";
import { authApi } from "../../lib/auth";

const ago = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    authApi.notifications
      .list()
      .then((d) => {
        setItems(d.items);
        setUnread(d.unread);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markAll = async () => {
    await authApi.notifications.markRead([]).catch(() => {});
    setItems((list) => list.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const openItem = async (n) => {
    if (!n.read) {
      authApi.notifications.markRead([n.id]).catch(() => {});
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if ((n.kind || "").startsWith("cis")) return navigate(`/app/compliance/${n.tenant_id}${n.control_id ? `?control=${n.control_id}` : ""}`);
    const params = n.kind === "changes" && n.prev_scan_id ? `?tab=compare&from=${n.prev_scan_id}&to=${n.scan_id}` : "";
    return navigate(`/app/tenants/${n.tenant_id}${params}`);
  };

  return (
    <div className="notif" ref={ref}>
      <button type="button" className="icon-btn notif-btn" onClick={() => setOpen((o) => !o)} aria-label="Notifications" aria-expanded={open} data-testid="notifications-btn">
        <Bell size={15} />
        {unread > 0 && (
          <span className="notif-badge" data-testid="notifications-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="notif-panel glass" data-testid="notifications-panel">
          <div className="notif-head">
            <b>Scan alerts</b>
            <span className="tenant-actions">
              <button type="button" className="icon-btn" onClick={load} aria-label="Refresh" data-testid="notifications-refresh">
                <RefreshCw size={13} />
              </button>
              {unread > 0 && (
                <button type="button" className="tb-btn sm ghost" onClick={markAll} data-testid="notifications-mark-read">
                  <CheckCheck size={13} /> Mark all read
                </button>
              )}
            </span>
          </div>
          {items.length === 0 && <p className="muted notif-empty" data-testid="notifications-empty">No alerts yet. Scheduled scans post here when seats, gaps or policies change.</p>}
          <ul className="notif-list">
            {items.map((n) => (
              <li key={n.id} className={`${n.read ? "" : "unread"} ${n.kind}`} data-testid={`notification-${n.id}`}>
                <button type="button" onClick={() => openItem(n)}>
                  <span className="notif-title">
                    {(n.kind === "error" || n.kind === "cis_error") && <AlertTriangle size={12} />}
                    {n.kind === "cis" && <ClipboardCheck size={12} />}
                    {n.kind === "cis_owner" && <UserCheck size={12} />} {n.title}
                  </span>
                  <span className="notif-lines">{n.lines.slice(0, 3).join(" · ")}</span>
                  <span className="notif-time">{ago(n.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
